class DDPDPGallery extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-dd-pdp-gallery-track]');
    this.slides = Array.from(this.querySelectorAll('[data-dd-pdp-gallery-slide]'));
    this.thumbnails = Array.from(this.querySelectorAll('[data-dd-pdp-gallery-thumbnail]'));
    this.thumbnailTrack = this.querySelector('[data-dd-pdp-gallery-thumbnails]');
    this.previousButton = this.querySelector('[data-dd-pdp-gallery-previous]');
    this.nextButton = this.querySelector('[data-dd-pdp-gallery-next]');
    this.progress = this.querySelector('[data-dd-carousel-progress]');
    this.indicator = this.querySelector('[data-dd-carousel-indicator]');
    this.status = this.querySelector('[data-dd-pdp-gallery-status]');
    this.activeIndex = 0;
    this.modalIndex = 0;
    this.dragState = null;
    this.suppressClick = false;
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    if (!this.track || this.slides.length === 0) return;

    this.track.addEventListener('scroll', () => this.queueGalleryUpdate(), { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.finishGallerySync(), { signal });
    this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.track.addEventListener('pointermove', (event) => this.onPointerMove(event), { signal });
    this.track.addEventListener('pointerup', (event) => this.onPointerUp(event), { signal });
    this.track.addEventListener('pointercancel', (event) => this.onPointerUp(event), { signal });
    this.track.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true, signal });
    this.track.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false, signal });
    this.track.addEventListener('touchend', () => this.onTouchEnd(), { signal });
    this.track.addEventListener('touchcancel', () => this.onTouchEnd(true), { signal });
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('click', (event) => this.guardDraggedClick(event), { capture: true, signal });

    this.resizeObserver = new ResizeObserver(() => this.refreshGeometry());
    this.resizeObserver.observe(this.track);

    this.connectModal(signal);
    this.updateGalleryState(false);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.modalObserver?.disconnect();
    window.clearTimeout(this.gallerySyncTimer);
    window.clearTimeout(this.swipeClickTimer);
    this.touchState = null;
    this.track?.style.removeProperty('scroll-snap-type');
    this.restoreUnderlyingContent();
    if (this.modal && this.nativeModalShow) this.modal.show = this.nativeModalShow;
    if (this.modal && this.nativeModalHide) this.modal.hide = this.nativeModalHide;
  }

  get reducedMotion() {
    return this.reducedMotionQuery.matches;
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - 1);
  }

  onClick(event) {
    const previous = event.target.closest('[data-dd-pdp-gallery-previous]');
    if (previous) {
      event.preventDefault();
      event.stopPropagation();
      this.setActiveIndex(this.activeIndex - 1, true, true);
      return;
    }

    const next = event.target.closest('[data-dd-pdp-gallery-next]');
    if (next) {
      event.preventDefault();
      event.stopPropagation();
      this.setActiveIndex(this.activeIndex + 1, true, true);
      return;
    }

    const thumbnail = event.target.closest('[data-dd-pdp-gallery-thumbnail]');
    if (thumbnail) {
      event.preventDefault();
      this.setActiveIndex(Number.parseInt(thumbnail.dataset.galleryIndex, 10), true, true);
      return;
    }

    const modalTrigger = event.target.closest('modal-opener button[data-media-id]');
    if (modalTrigger && this.contains(modalTrigger)) {
      this.setupModal(this.abortController.signal);
      const slide = modalTrigger.closest('[data-dd-pdp-gallery-slide]');
      const index = Number.parseInt(slide?.dataset.galleryIndex || String(this.activeIndex), 10);
      this.desiredModalIndex = index;
      window.requestAnimationFrame(() => this.lockModalToIndex(index));
    }
  }

  guardDraggedClick(event) {
    if (!this.suppressClick || !event.target.closest('modal-opener')) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.suppressClick = false;
  }

  onTouchStart(event) {
    if (event.touches.length !== 1) {
      this.onTouchEnd(true);
      return;
    }
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    if (event.target.closest('video, iframe, model-viewer, .deferred-media, .product-model')) return;
    window.clearTimeout(this.swipeClickTimer);
    this.suppressClick = false;
    const touch = event.touches[0];
    this.touchState = {
      startX: touch.clientX,
      startY: touch.clientY,
      startIndex: this.gallerySyncLocked ? this.activeIndex : this.nearestIndex(this.track, this.slides),
      startScrollLeft: this.track.scrollLeft,
      distance: 0,
      horizontal: false,
    };
  }

  onTouchMove(event) {
    const state = this.touchState;
    if (!state) return;
    if (event.touches.length !== 1) {
      this.onTouchEnd(true);
      return;
    }
    const dx = event.touches[0].clientX - state.startX;
    const dy = event.touches[0].clientY - state.startY;
    if (!state.horizontal) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < 6) return;
      if (Math.abs(dx) <= Math.abs(dy) * 1.2 || !event.cancelable) {
        this.touchState = null;
        return;
      }
      state.horizontal = true;
      window.clearTimeout(this.gallerySyncTimer);
      this.gallerySyncLocked = true;
      this.track.style.scrollSnapType = 'none';
      this.track.scrollTo({ left: state.startScrollLeft, behavior: 'instant' });
    }
    if (event.cancelable) event.preventDefault();
    state.distance = dx;
    this.track.scrollTo({ left: state.startScrollLeft - dx, behavior: 'instant' });
  }

  onTouchEnd(cancelled = false) {
    const state = this.touchState;
    this.touchState = null;
    if (!state?.horizontal) return;
    this.track.style.removeProperty('scroll-snap-type');
    const direction = !cancelled && Math.abs(state.distance) >= 24 ? (state.distance < 0 ? 1 : -1) : 0;
    this.suppressClick = true;
    this.setActiveIndex(state.startIndex + direction, true, true);
    this.swipeClickTimer = window.setTimeout(() => {
      this.suppressClick = false;
    }, 400);
  }

  onPointerDown(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (
      event.target.closest(
        '[data-dd-pdp-gallery-previous], [data-dd-pdp-gallery-next], .deferred-media__poster, video, iframe, model-viewer',
      )
    ) {
      return;
    }

    this.finishGallerySync();

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.track.scrollLeft,
      moved: false,
    };
  }

  onPointerMove(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
    const distance = event.clientX - this.dragState.startX;
    if (!this.dragState.moved && Math.abs(distance) < 6) return;

    this.dragState.moved = true;
    this.track.toggleAttribute('data-dragging', true);
    this.track.style.scrollSnapType = 'none';
    this.track.setPointerCapture?.(event.pointerId);
    this.track.scrollLeft = this.dragState.startScrollLeft - distance;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
    const moved = this.dragState.moved;
    this.dragState = null;
    this.track.removeAttribute('data-dragging');
    this.track.style.removeProperty('scroll-snap-type');
    if (this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);

    if (moved) {
      this.suppressClick = true;
      this.setActiveIndex(this.nearestIndex(this.track, this.slides), true, true);
      window.setTimeout(() => {
        this.suppressClick = false;
      }, 0);
    }
  }

  queueGalleryUpdate() {
    if (this.galleryFrame) return;
    this.galleryFrame = window.requestAnimationFrame(() => {
      this.galleryFrame = null;
      if (this.gallerySyncLocked) {
        this.updateProgress();
        return;
      }
      const nextIndex = this.nearestIndex(this.track, this.slides);
      if (nextIndex !== this.activeIndex) {
        this.activeIndex = nextIndex;
        this.updateGalleryState(true);
      } else {
        this.updateProgress();
      }
    });
  }

  nearestIndex(track, items) {
    if (!track || items.length === 0) return 0;
    const target = track.scrollLeft;
    let nearest = 0;
    let distance = Number.POSITIVE_INFINITY;

    items.forEach((item, index) => {
      const currentDistance = Math.abs(item.offsetLeft - target);
      if (currentDistance < distance) {
        distance = currentDistance;
        nearest = index;
      }
    });

    return nearest;
  }

  setActiveIndex(index, scroll = true, announce = false) {
    const nextIndex = Math.max(0, Math.min(this.maxIndex, Number.isFinite(index) ? index : 0));
    this.activeIndex = nextIndex;

    if (scroll) {
      this.gallerySyncLocked = true;
      window.clearTimeout(this.gallerySyncTimer);
      this.track.scrollTo({
        left: this.slides[nextIndex].offsetLeft,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
      this.gallerySyncTimer = window.setTimeout(() => this.finishGallerySync(), this.reducedMotion ? 0 : 500);
    }

    this.updateGalleryState(announce);
  }

  finishGallerySync() {
    if (this.touchState?.horizontal || !this.gallerySyncLocked) return;
    window.clearTimeout(this.gallerySyncTimer);
    this.gallerySyncLocked = false;
    const settledIndex = this.nearestIndex(this.track, this.slides);
    if (settledIndex !== this.activeIndex) {
      this.activeIndex = settledIndex;
      this.updateGalleryState(false);
    } else {
      this.updateProgress();
    }
  }

  updateGalleryState(announce) {
    this.slides.forEach((slide, index) => {
      const active = index === this.activeIndex;
      slide.classList.toggle('is-active', active);
      slide.querySelectorAll('modal-opener button[data-media-id]').forEach((button) => {
        button.tabIndex = active ? 0 : -1;
      });
    });
    this.thumbnails.forEach((thumbnail, index) => {
      if (index === this.activeIndex) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    });

    this.previousButton && (this.previousButton.disabled = this.activeIndex === 0);
    this.nextButton && (this.nextButton.disabled = this.activeIndex === this.maxIndex);
    this.keepThumbnailVisible();
    this.updateProgress();
    this.updateActiveMediaPlayback();

    if (announce && this.status) {
      this.status.textContent = `${this.activeIndex + 1} of ${this.slides.length}`;
    }
  }

  keepThumbnailVisible() {
    const thumbnail = this.thumbnails[this.activeIndex];
    if (!thumbnail || !this.thumbnailTrack) return;
    const thumbnailStart = thumbnail.offsetLeft;
    const thumbnailEnd = thumbnailStart + thumbnail.offsetWidth;
    const visibleStart = this.thumbnailTrack.scrollLeft;
    const visibleEnd = visibleStart + this.thumbnailTrack.clientWidth;
    let destination = visibleStart;

    if (thumbnailStart < visibleStart) destination = thumbnailStart;
    else if (thumbnailEnd > visibleEnd) destination = thumbnailEnd - this.thumbnailTrack.clientWidth;
    else return;

    this.thumbnailTrack.scrollTo({ left: destination, behavior: this.reducedMotion ? 'auto' : 'smooth' });
  }

  updateProgress() {
    if (!this.progress || !this.indicator) return;
    const railWidth = this.progress.getBoundingClientRect().width || 160;
    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const position = maxScroll > 0 ? Math.max(0, Math.min(1, this.track.scrollLeft / maxScroll)) : 0;
    const visibleRatio = this.track.scrollWidth > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
    const indicatorWidth = Math.max(48, Math.min(railWidth, railWidth * visibleRatio));
    const available = Math.max(0, railWidth - indicatorWidth);
    this.indicator.style.setProperty('--dd-carousel-indicator-width', `${indicatorWidth}px`);
    this.indicator.style.setProperty('--dd-carousel-indicator-x', `${position * available}px`);
    this.progress.setAttribute('role', 'progressbar');
    this.progress.setAttribute('aria-valuemin', '0');
    this.progress.setAttribute('aria-valuemax', '100');
    this.progress.setAttribute('aria-valuenow', String(Math.round(position * 100)));
  }

  updateActiveMediaPlayback() {
    this.slides.forEach((slide, index) => {
      if (index === this.activeIndex) return;
      this.pauseMedia(slide, true);
    });
  }

  pauseMedia(container, reset = false) {
    const video = container?.querySelector('video');
    if (video) {
      video.pause();
      if (reset) {
        try {
          video.currentTime = 0;
        } catch (_error) {
          // Some streaming sources are not seekable until metadata is ready.
        }
      }
    }

    const iframe = container?.querySelector('iframe');
    iframe?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
    iframe?.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
  }

  refreshGeometry() {
    if (this.touchState?.horizontal || !this.slides[this.activeIndex]) return;
    this.track.scrollLeft = this.slides[this.activeIndex].offsetLeft;
    this.updateProgress();
    if (this.modal?.hasAttribute('open')) this.setModalIndex(this.modalIndex, false);
  }

  async connectModal(signal) {
    await customElements.whenDefined('product-modal');
    if (signal.aborted) return;

    this.setupModal(signal);
  }

  setupModal(signal) {
    if (this.modalConnected) return;

    this.modal = document.getElementById(this.dataset.modalId);
    if (!this.modal) return;

    this.modalTrack = this.modal.querySelector('[data-dd-pdp-modal-track]');
    this.modalItems = Array.from(this.modal.querySelectorAll('[data-dd-pdp-modal-item]'));
    this.modalPrevious = this.modal.querySelector('[data-dd-pdp-modal-previous]');
    this.modalNext = this.modal.querySelector('[data-dd-pdp-modal-next]');
    this.modalZoom = this.modal.querySelector('[data-dd-pdp-modal-zoom]');
    this.modalZoomLabel = this.modal.querySelector('[data-dd-pdp-modal-zoom-label]');
    this.modalConnected = true;
    this.modal.dataset.ddPdpGalleryControlled = 'true';
    this.nativeModalShow = this.modal.show.bind(this.modal);
    this.modal.show = (opener) => {
      const openedMediaId = opener?.dataset.mediaId;
      const openedIndex = this.modalItems.findIndex((item) => item.dataset.mediaId === openedMediaId);
      this.desiredModalIndex = openedIndex >= 0 ? openedIndex : this.activeIndex;
      this.prepareModalNeighborhood(this.desiredModalIndex);
      this.modalSyncLocked = true;
      this.nativeModalShow(opener);
      this.lockModalToIndex(this.desiredModalIndex);
    };

    this.nativeModalHide = this.modal.hide.bind(this.modal);
    this.modal.hide = () => {
      this.restoreUnderlyingContent();
      this.nativeModalHide();
    };

    this.modalPrevious?.addEventListener('click', (event) => this.onModalControl(event, -1), { signal });
    this.modalNext?.addEventListener('click', (event) => this.onModalControl(event, 1), { signal });
    this.modalZoom?.addEventListener('click', (event) => this.onModalZoom(event), { signal });
    this.modal.addEventListener('keydown', (event) => this.onModalKeydown(event), { signal });
    this.modalTrack?.addEventListener('scroll', () => this.queueModalUpdate(), { passive: true, signal });

    this.modal.querySelectorAll('[data-dd-pdp-modal-control], [data-dd-pdp-modal-item]').forEach((element) => {
      element.addEventListener('pointerup', (event) => event.stopPropagation(), { signal });
    });

    this.modalItems.forEach((item) => {
      item.addEventListener('click', (event) => {
        if (event.target.matches('img') && item.dataset.mediaType === 'image') this.toggleModalZoom();
      }, { signal });
    });

    this.querySelectorAll('modal-opener button[data-media-id]').forEach((trigger) => {
      const prepare = () => {
        const index = this.modalItems.findIndex((item) => item.dataset.mediaId === trigger.dataset.mediaId);
        if (index >= 0) this.prepareModalNeighborhood(index);
      };
      trigger.addEventListener('pointerenter', prepare, { once: true, signal });
      trigger.addEventListener('focus', prepare, { once: true, signal });
      trigger.addEventListener('touchstart', prepare, { once: true, passive: true, signal });
    });

    this.modalObserver = new MutationObserver(() => this.onModalStateChange());
    this.modalObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
  }

  onModalControl(event, direction) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.lockModalToIndex(this.modalIndex + direction);
  }

  onModalKeydown(event) {
    if (!this.modal.hasAttribute('open')) return;
    if (event.code === 'ArrowLeft') {
      event.preventDefault();
      this.lockModalToIndex(this.modalIndex - 1);
    } else if (event.code === 'ArrowRight') {
      event.preventDefault();
      this.lockModalToIndex(this.modalIndex + 1);
    }
  }

  queueModalUpdate() {
    if (this.modalSyncLocked) return;
    if (this.modalFrame) return;
    this.modalFrame = window.requestAnimationFrame(() => {
      this.modalFrame = null;
      const index = this.nearestIndex(this.modalTrack, this.modalItems);
      if (index !== this.modalIndex) this.setModalIndex(index, false, false);
    });
  }

  setModalIndex(index, scroll = true, align = true) {
    if (!this.modalTrack || this.modalItems.length === 0) return;
    this.modalIndex = Math.max(0, Math.min(this.modalItems.length - 1, Number.isFinite(index) ? index : 0));
    this.modal.dataset.activeMediaIndex = String(this.modalIndex);
    this.prepareModalNeighborhood(this.modalIndex);
    this.resetModalZoom();

    this.modalItems.forEach((item, itemIndex) => item.classList.toggle('active', itemIndex === this.modalIndex));
    if (scroll) {
      this.modalTrack.scrollTo({
        left: this.modalItems[this.modalIndex].offsetLeft,
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
    } else if (align) {
      this.modalTrack.scrollLeft = this.modalItems[this.modalIndex].offsetLeft;
    }

    this.modalPrevious && (this.modalPrevious.disabled = this.modalIndex === 0);
    this.modalNext && (this.modalNext.disabled = this.modalIndex === this.modalItems.length - 1);
    const imageActive = this.modalItems[this.modalIndex].dataset.mediaType === 'image';
    if (this.modalZoom) this.modalZoom.hidden = !imageActive;

    this.modalItems.forEach((item, itemIndex) => {
      if (itemIndex !== this.modalIndex) this.pauseMedia(item, true);
    });

    this.activeIndex = this.modalIndex;
    this.updateGalleryState(false);
  }

  prepareModalNeighborhood(index) {
    this.prepareModalItem(index, 'high');
    this.prepareModalItem(index - 1, 'auto');
    this.prepareModalItem(index + 1, 'auto');
  }

  prepareModalItem(index, priority = 'auto') {
    const item = this.modalItems?.[index];
    if (!item || item.dataset.mediaType !== 'image') return;

    const stage = item.querySelector('[data-dd-pdp-modal-stage]');
    const template = stage?.querySelector('template[data-dd-pdp-modal-image]');
    let image = stage?.querySelector('img');
    if (image && template && item.dataset.ddMediaError === 'true') {
      image.remove();
      image = null;
      delete item.dataset.ddMediaPrepared;
      delete item.dataset.ddMediaReady;
      delete item.dataset.ddMediaError;
    }
    if (!image && template) {
      image = template.content.querySelector('img')?.cloneNode(true);
      if (image) image.dataset.ddFullSrc = template.dataset.ddFullSrc;
    }
    if (!image) return;

    image.loading = 'eager';
    image.fetchPriority = priority;
    if (template && image.dataset.ddFullResolution !== 'true') {
      const width = stage.getBoundingClientRect().width;
      if (width > 0) image.sizes = `${Math.ceil(width)}px`;
    }

    const isCurrentImage = () => !this.abortController.signal.aborted && stage.contains(image);
    const markReady = () => {
      if (isCurrentImage() && image.naturalWidth > 0) {
        item.dataset.ddMediaReady = 'true';
        item.removeAttribute('data-dd-media-error');
      }
    };
    const markError = () => {
      if (isCurrentImage() && image.naturalWidth === 0) {
        item.dataset.ddMediaError = 'true';
        delete item.dataset.ddMediaReady;
      }
    };

    if (item.dataset.ddMediaPrepared !== 'true') {
      item.dataset.ddMediaPrepared = 'true';
      const { signal } = this.abortController;
      image.addEventListener('load', markReady, { signal });
      image.addEventListener('error', markError, { signal });
      if (!stage.contains(image)) stage.appendChild(image);
    }

    if (image.complete) {
      if (image.naturalWidth > 0) markReady();
      else markError();
    }

    image.decode?.().then(markReady).catch(() => {
      if (image.complete) {
        if (image.naturalWidth > 0) markReady();
        else markError();
      }
    });
  }

  prepareModalZoomImage(item) {
    const image = item.querySelector('[data-dd-pdp-modal-stage] > img');
    if (!image || image.dataset.ddFullResolution === 'true' || !image.dataset.ddFullSrc) return;

    const isCurrentZoom = () =>
      !this.abortController.signal.aborted &&
      this.modal?.hasAttribute('open') &&
      this.modalItems[this.modalIndex] === item &&
      item.dataset.ddZoomed === 'true' &&
      item.contains(image);

    if (!image.ddFullImagePromise) {
      const fullImage = new Image();
      fullImage.className = image.className;
      fullImage.alt = image.alt;
      fullImage.decoding = 'async';
      fullImage.fetchPriority = 'high';
      fullImage.dataset.ddFullResolution = 'true';
      delete item.dataset.ddZoomError;
      image.ddFullImagePromise = new Promise((resolve, reject) => {
        fullImage.onload = async () => {
          try {
            await fullImage.decode?.();
          } catch (_error) {
            if (!fullImage.naturalWidth) {
              reject(new Error('Modal zoom image could not be decoded'));
              return;
            }
          }
          resolve(fullImage);
        };
        fullImage.onerror = () => reject(new Error('Modal zoom image could not be loaded'));
        fullImage.src = image.dataset.ddFullSrc;
      });
    }

    image.ddFullImagePromise.then((fullImage) => {
      if (!isCurrentZoom()) return;
      image.replaceWith(fullImage);
      this.centerModalZoom(item);
      item.dataset.ddMediaReady = 'true';
      delete item.dataset.ddMediaError;
    }).catch(() => {
      image.ddFullImagePromise = null;
      if (!isCurrentZoom()) return;
      item.dataset.ddZoomError = 'true';
      this.resetModalZoom();
    });
  }

  lockModalToIndex(index) {
    if (!this.modalTrack || this.modalItems.length === 0) return;
    const syncToken = (this.modalSyncToken || 0) + 1;
    this.modalSyncToken = syncToken;
    this.modalSyncLocked = true;
    this.modalTrack.style.scrollBehavior = 'auto';
    this.setModalIndex(index, false);
    window.requestAnimationFrame(() => {
      this.setModalIndex(index, false);
      window.requestAnimationFrame(() => {
        if (this.modalSyncToken !== syncToken) return;
        this.modalTrack.style.removeProperty('scroll-behavior');
        this.modalSyncLocked = false;
      });
    });
  }

  onModalZoom(event) {
    event.preventDefault();
    event.stopPropagation();
    this.toggleModalZoom();
  }

  centerModalZoom(item) {
    window.requestAnimationFrame(() => {
      if (!this.modal?.hasAttribute('open') || item.dataset.ddZoomed !== 'true' || this.modalItems[this.modalIndex] !== item) return;
      const stage = item.querySelector('[data-dd-pdp-modal-stage]');
      if (!stage) return;
      stage.scrollLeft = Math.max(0, (stage.scrollWidth - stage.clientWidth) / 2);
      stage.scrollTop = Math.max(0, (stage.scrollHeight - stage.clientHeight) / 2);
    });
  }

  toggleModalZoom() {
    const activeItem = this.modalItems?.[this.modalIndex];
    if (!activeItem || activeItem.dataset.mediaType !== 'image') return;
    const zoomed = activeItem.dataset.ddZoomed !== 'true';
    activeItem.dataset.ddZoomed = String(zoomed);
    if (zoomed) {
      this.centerModalZoom(activeItem);
      this.prepareModalZoomImage(activeItem);
    }
    if (this.modalZoom) {
      this.modalZoom.setAttribute('aria-pressed', String(zoomed));
      this.modalZoom.setAttribute('aria-label', zoomed ? 'Reset image zoom' : 'View image at full size');
    }
    if (this.modalZoomLabel) this.modalZoomLabel.textContent = zoomed ? 'Fit image' : 'View full size';
    if (!zoomed) {
      const activeStage = activeItem.querySelector('[data-dd-pdp-modal-stage]');
      if (activeStage) {
        activeStage.scrollTop = 0;
        activeStage.scrollLeft = 0;
      }
    }
  }

  resetModalZoom() {
    this.modalItems?.forEach((item) => {
      item.removeAttribute('data-dd-zoomed');
      const stage = item.querySelector('[data-dd-pdp-modal-stage]');
      if (stage) {
        stage.scrollTop = 0;
        stage.scrollLeft = 0;
      }
    });
    if (this.modalZoom) {
      this.modalZoom.setAttribute('aria-pressed', 'false');
      this.modalZoom.setAttribute('aria-label', 'View image at full size');
    }
    if (this.modalZoomLabel) this.modalZoomLabel.textContent = 'View full size';
  }

  onModalStateChange() {
    if (this.modal.hasAttribute('open')) {
      const openedMediaId = this.modal.openedBy?.dataset.mediaId;
      const openedIndex = this.modalItems.findIndex((item) => item.dataset.mediaId === openedMediaId);
      const requestedIndex = openedIndex >= 0 ? openedIndex : (this.desiredModalIndex ?? this.activeIndex);
      this.lockModalToIndex(requestedIndex);
      this.makeUnderlyingContentInert();
    } else {
      this.restoreUnderlyingContent();
      this.resetModalZoom();
      this.setActiveIndex(this.modalIndex, true, false);
    }
  }

  makeUnderlyingContentInert() {
    this.inertedElements = [];
    Array.from(document.body.children).forEach((element) => {
      if (element === this.modal || ['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName)) return;
      if (!element.hasAttribute('inert')) {
        element.setAttribute('inert', '');
        this.inertedElements.push(element);
      }
    });
  }

  restoreUnderlyingContent() {
    this.inertedElements?.forEach((element) => element.removeAttribute('inert'));
    this.inertedElements = [];
  }
}

if (!customElements.get('dd-pdp-gallery')) customElements.define('dd-pdp-gallery', DDPDPGallery);
